import * as THREE from 'three';
import { ThreeEvent } from '@react-three/fiber';
import { BaseTool } from '../BaseTool';
import { WallElement, DoorElement, WindowElement, BIMElement } from '../../core/types';
import { computeWallMoveUpdates, createTranslatedWallCopy } from '../../utils/WallGeometryUtils';
import { transformSceneObjects, copySceneObjects } from '../../core/scene/editRouter';
import { getSnapManager, constrainDraftPoint } from '../snapping';
import {
    applyJunctionStretch,
    collectSceneJunction,
    junctionGrabTolerance,
    type SceneJunction,
} from '../../utils/JunctionMoveUtils';

/**
 * Move Tool - BIM 2025 Style
 *
 * Moves selected elements from one point to another.
 * Supports:
 * - Single and multi-element selection (pick-first when nothing is selected)
 * - BIM-style hover cursor ('move' over a movable element)
 * - Live displacement preview while traveling to the destination
 * - Joint-aware wall moves: walls connected at a shared endpoint stretch
 *   along their own alignment and follow the moved wall; unconnected walls
 *   move free
 * - SketchUp-style junction moves: with NOTHING selected, a click that
 *   resolves onto a shared defining point (wall runs meeting end to end, a
 *   column standing on a wall corner, a CAD line endpoint touching a sketch
 *   edge, ...) grabs THE POINT — every welded object stretches from it to
 *   the destination instead of riding along rigidly
 * - Constraint mode (horizontal/vertical via Shift)
 * - Copy mode (Ctrl key)
 *
 * Workflow:
 * 1. Select elements (or click one to select it) — or click a shared corner
 *    with nothing selected to grab the junction
 * 2. Click base point
 * 3. Click destination point
 */
export class MoveToolClass extends BaseTool {
    id = 'MOVE';
    name = 'Move';
    // Base cursor is the standard arrow; hovering a movable element switches
    // it to 'move' (BIM behavior) via appStore.setCursor.
    cursor = 'default';

    private basePoint: THREE.Vector3 | null = null;
    private currentPoint: THREE.Vector3 | null = null;
    private copyMode: boolean = false;
    // Latch guards so holding Ctrl doesn't retoggle copy mode on key repeat.
    private ctrlLatched: boolean = false;
    // Active junction grab (SketchUp vertex move): set when the base click
    // resolves onto a shared defining point with an empty selection. The
    // commit then stretches every welded object from the junction instead
    // of rigid-translating the selection.
    private junction: SceneJunction | null = null;
    private selectedElements: BIMElement[] = [];
    // B4 cross-workbench: selection ids owned by OTHER workbenches (CAD
    // entities, sketch citizens) — routed through the edit router at commit.
    private foreignIds: string[] = [];
    // Dedupe guard: identical consecutive preview snapshots skip the store
    // write, so pointer motion without a snapped-coordinate change never
    // re-renders the preview layer.
    private lastPreviewKey: string | null = null;
    // Hover cursor pick throttle: a full-scene raycast at most every
    // HOVER_PICK_INTERVAL_MS while traveling, never while an op is active.
    private static readonly HOVER_PICK_INTERVAL_MS = 80;
    private lastHoverPick: number = 0;
    private lastHoverClient: { x: number; y: number } | null = null;

    activate(context: any): void {
        super.activate(context);
        this.loadSelection();

        if (this.selectedElements.length === 0) {
            console.log('Move: No elements selected. Click to select elements first.');
        } else {
            console.log('Move: Selected', this.selectedElements.length, 'elements. Click base point.');
        }
    }

    private loadSelection(): void {
        if (!this.context) return;

        const selectedIds = this.context.appStore.selectedIds || [];
        this.selectedElements = [];
        this.foreignIds = [];

        for (const id of selectedIds) {
            const element = this.context.bimStore.project.elements.get(id);
            if (element) {
                this.selectedElements.push(element);
            } else {
                this.foreignIds.push(id);
            }
        }
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        // Pick-first: with nothing selected, the click selects a movable
        // element and the next two clicks complete the move — UNLESS the
        // click resolves onto a junction: grabbing the shared point wins
        // over picking (SketchUp vertex-grab precedence).
        if (this.selectedElements.length === 0) {
            const junction = this.detectJunction(event);
            if (junction) {
                this.junction = junction;
                this.basePoint = junction.point.clone();
                this.currentPoint = this.basePoint;
                this.lastPreviewKey = null;
                this.context.appStore.setCursor('move');
                this.writePreview();
                console.log('Move: Junction grab —', junction.participants.length, 'joined object(s) at the shared point');
                return;
            }

            const id = this.findElementIdAtPoint(event);
            if (id && this.isMovableElement(id)) {
                this.selectElement(id);
                this.loadSelection();
            }
            return;
        }

        const point = this.getIntersection(event);
        if (!point) return;
        const snappedPoint = this.snapToGrid(point);

        if (!this.basePoint) {
            // First click: Base point
            this.basePoint = snappedPoint.clone();
            this.currentPoint = this.basePoint;
            this.lastPreviewKey = null;
            // The whole gesture reads as a move, independent of hover.
            this.context.appStore.setCursor('move');
            this.writePreview();
        } else {
            // Second click: Destination
            const destination = this.junction
                ? this.applyConstraint(constrainDraftPoint(snappedPoint, this.context.camera, this.context.renderer, this.basePoint))
                : this.applyConstraint(snappedPoint);
            const translation = destination.clone().sub(this.basePoint);

            if (translation.lengthSq() > 1e-9) {
                if (this.junction) {
                    // Junction move: copy mode does not apply — the gesture
                    // deforms the welded geometry instead of cloning it.
                    if (this.copyMode) {
                        console.log('Move: Copy mode is ignored for junction moves');
                    }
                    const movedCount = applyJunctionStretch(this.junction.point, translation, 'Move Junction');
                    console.log('Move: Junction move stretched', movedCount, 'object(s) from the shared point');
                } else if (this.copyMode) {
                    this.copyElements(translation);
                } else {
                    this.commitMove(translation);
                }
            }

            // Reset for next operation (Move returns to Select, keeping the
            // selection — BIM behavior).
            this.reset();
            this.context.appStore.setActiveTool('select');
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        if (this.basePoint) {
            // Live displacement feedback while traveling to the destination.
            const point = this.getIntersection(event);
            if (!point) return;

            // Junction moves resolve through the full drafting pipeline
            // (grid/polar/osnap/ortho — the grips' pipeline): snapping the
            // shared point onto another defining point is the core
            // SketchUp-style workflow.
            this.currentPoint = this.junction
                ? this.applyConstraint(constrainDraftPoint(point, this.context.camera, this.context.renderer, this.basePoint))
                : this.applyConstraint(this.snapToGrid(point));
            this.writePreview();
            return;
        }

        this.updateHoverCursor(event);
    }

    onPointerUp(): void { }

    onKeyDown(event: KeyboardEvent): void {
        // Toggle copy mode on a physical Ctrl press (latched against repeats)
        if (event.key === 'Control' && !this.ctrlLatched) {
            this.ctrlLatched = true;
            this.copyMode = !this.copyMode;
            console.log('Move: Copy mode', this.copyMode ? 'ON' : 'OFF');
        }

        if (event.key === 'Escape') {
            this.reset();
            this.context?.appStore.setActiveTool('select');
        }
    }

    onKeyUp(event: KeyboardEvent): void {
        super.onKeyUp?.(event);
        if (event.key === 'Control') {
            this.ctrlLatched = false;
        }
    }

    private applyConstraint(point: THREE.Vector3): THREE.Vector3 {
        if (!this.basePoint) return point;

        // Shift (BaseTool.isOrthoMode) constrains the displacement to the
        // dominant axis of the current direction, like BIM's ortho lock.
        if (this.isOrthoMode) {
            return this.snapToOrtho(point, this.basePoint);
        }

        return point;
    }

    // ==================== Hover Cursor ====================

    /**
     * BIM-style hover feedback: while no move gesture is active, pick the
     * element under the cursor (throttled) and switch the viewport cursor to
     * 'move' over anything draggable, back to the arrow elsewhere.
     */
    private updateHoverCursor(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        const native = event.nativeEvent;
        if (this.lastHoverClient &&
            this.lastHoverClient.x === native.clientX &&
            this.lastHoverClient.y === native.clientY) return;

        const now = performance.now();
        if (now - this.lastHoverPick < MoveToolClass.HOVER_PICK_INTERVAL_MS) return;
        this.lastHoverPick = now;
        this.lastHoverClient = { x: native.clientX, y: native.clientY };

        const id = this.findElementIdAtPoint(event);
        const movable = id ? this.isMovableElement(id) : false;
        this.context.appStore.setCursor(movable ? 'move' : this.cursor);
    }

    private isMovableElement(id: string): boolean {
        if (!this.context) return false;
        const element = this.context.bimStore.project.elements.get(id);
        if (!element) return false;
        if (element.parameters?.['Pinned']) return false;
        return element.category !== 'Levels' && element.category !== 'Grids';
    }

    // ==================== Junction Grab ====================

    /**
     * Resolve a click into a grabbable junction (shared defining point).
     * The scan origin is the ground-plane intersection; ground-level defining
     * points also match along their whole vertical corner edge, so a wall
     * corner is grabbable at any click height. CAD endpoints arrive through
     * the OSNAP manager when one is under the cursor.
     */
    private detectJunction(event: ThreeEvent<PointerEvent>): SceneJunction | null {
        if (!this.context) return null;
        const point = this.getIntersection(event);
        if (!point) return null;

        const grabTol = junctionGrabTolerance(this.context.camera, point);
        const osnap = getSnapManager().getBestSnap(point, this.context.camera, this.context.renderer);
        const scanOrigin = osnap?.type === 'endpoint' ? osnap.position : point;
        return collectSceneJunction(scanOrigin, grabTol);
    }

    // ==================== Preview ====================

    private previewKey(): string {
        const b = this.basePoint;
        const c = this.currentPoint;
        const bx = b ? b.x.toFixed(4) : 'n';
        const by = b ? b.y.toFixed(4) : 'n';
        const cx = c ? c.x.toFixed(4) : 'n';
        const cy = c ? c.y.toFixed(4) : 'n';
        return `${bx},${by}|${cx},${cy}|${this.copyMode ? 1 : 0}`;
    }

    private writePreview(): void {
        if (!this.context || !this.basePoint || !this.currentPoint) return;

        const key = this.previewKey();
        if (key === this.lastPreviewKey) return;
        this.lastPreviewKey = key;

        // moveDrag shape reuses the shared dashed displacement preview.
        this.context.appStore.setToolPreview({
            active: true,
            toolId: this.id,
            moveDrag: true,
            basePoint: this.basePoint,
            current: this.currentPoint,
            copyMode: this.copyMode
        });
    }

    // ==================== Commit ====================

    /**
     * Commit a move: walls go through the joint-aware translation (connected
     * walls stretch along their alignment, render geometry is re-solved) and
     * everything commits as ONE batched update — one undo entry, one store
     * refresh.
     */
    private commitMove(translation: THREE.Vector3): void {
        if (!this.context) return;

        const elements = this.context.bimStore.project.elements;
        const wallIds = this.selectedElements
            .filter(e => e.category === 'Walls')
            .map(e => e.id);

        const batch: Array<{ id: string; updates: Partial<BIMElement> }> = [];
        if (wallIds.length > 0) {
            const wallUpdates = computeWallMoveUpdates(elements, wallIds, translation);
            wallUpdates.forEach((updates, id) => batch.push({ id, updates }));
        }

        this.reportUnsupportedElements();

        if (batch.length > 0) {
            this.context.bimStore.updateElements(batch);
            console.log('Move: Moved', batch.length, 'walls by', translation);
        }

        // B4 cross-workbench: the rest of the selection (CAD entities, sketch
        // citizens) moves through the edit router — one matrix, each owner
        // commits into its own store and undo chain.
        if (this.foreignIds.length > 0) {
            const moved = transformSceneObjects(
                this.foreignIds,
                new THREE.Matrix4().makeTranslation(translation.x, translation.y, translation.z),
                'Move'
            );
            console.log('Move: Moved', moved, 'cross-workbench object(s)');
        }
    }

    private copyElements(translation: THREE.Vector3): void {
        if (!this.context) return;

        this.reportUnsupportedElements();

        const newIds: string[] = [];
        for (const element of this.selectedElements) {
            if (element.category !== 'Walls') continue;
            const copy = createTranslatedWallCopy(element as WallElement, translation);
            this.context.bimStore.addElement(copy);
            newIds.push(copy.id);
        }

        // B4 cross-workbench: foreign citizens copy through the router and
        // join the post-copy selection.
        if (this.foreignIds.length > 0) {
            newIds.push(...copySceneObjects(
                this.foreignIds,
                new THREE.Matrix4().makeTranslation(translation.x, translation.y, translation.z),
                'Copy'
            ));
        }

        if (newIds.length > 0) {
            // Select the new copies
            this.context.appStore.select(newIds);
            this.context.bimStore.setSelection(newIds);
            console.log('Move: Copied', newIds.length, 'objects');
        }
    }

    /**
     * Hosted elements follow their host wall parametrically, so they only
     * move when their host is part of the move. Anything else is out of
     * scope for the move tool for now.
     */
    private reportUnsupportedElements(): void {
        if (!this.context) return;

        for (const element of this.selectedElements) {
            if (element.category === 'Walls') continue;

            const hostId = (element as DoorElement | WindowElement).geometryData?.hostWallId;
            const hostIsMoving = hostId
                ? this.selectedElements.some(e => e.id === hostId)
                : false;

            if (!hostIsMoving) {
                console.warn('Move: Unsupported element category', element.category);
            }
        }
    }

    private reset(): void {
        this.basePoint = null;
        this.currentPoint = null;
        this.copyMode = false;
        this.ctrlLatched = false;
        this.junction = null;
        this.lastPreviewKey = null;
        this.context?.appStore.setToolPreview(null);
    }

    deactivate(): void {
        // Restore the base cursor while the context is still available.
        this.context?.appStore.setCursor(this.cursor);
        super.deactivate();
        this.reset();
        this.selectedElements = [];
        this.lastHoverPick = 0;
        this.lastHoverClient = null;
    }
}
